import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AddButton } from '../ui/AddButton';
import { shownSpaceColor } from '@dpw/shared';
import { AnimatePresence } from 'motion/react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SessionUser } from '@dpw/shared';
import { api } from '../api';
import { useSpaces, useSummary } from '../lib/queries';
import { IconBell, IconBook, IconCalendar, IconChat, IconClock, IconFolder, IconGear, IconHome, IconInbox, IconMoon, IconRec, IconSearch, IconSun } from '../ui/icons';
import { CommandPalette, openPalette } from '../ui/CommandPalette';
import { Count, Orb, Glide } from '../ui/motion';
import { modKey } from '../lib/keys';
import { Avatar } from '../ui/bits';
import { usePhone } from '../lib/media';
import { PhoneShell } from '../phone/PhoneShell';
import { usePresence } from '../lib/presence';
import { usePushMessages } from '../lib/push';
import { AssistantPanelProvider } from '../assistant/Panel';
import { VoiceControlProvider, useVoiceControl } from '../voice/VoiceControl';
import { AiButton, AiScopeProvider } from '../assistant/AiButton';
import { ActivityOverlay, DockArea, DockProvider } from './Dock';
import { SpaceSheet, createsSpaces } from '../ui/SpaceForm';
import { NewDot } from '../ui/NewDot';
import { useNewSince, useSeenOnVisit } from '../lib/seen';

const ROLE = { partner: 'Partner', director: 'Director', member: 'Member' } as const;

export function Shell(p: { me: SessionUser; theme: 'light' | 'dark'; onTheme: () => void; children: ReactNode }) {
  usePresence();
  usePushMessages();
  useSeenOnVisit();
  // A phone gets the phone's frame (tab bar, top bars per page); everything wider gets the sidebar.
  // The assistant panel opens over either, from any page (useAssistantPanel).
  // The dock (the AI and activity panels pushing the page on a wide screen) holds them; the page's AI scope is read by
  // the AI panel (it follows the page unless pinned).
  return (
    <DockProvider me={p.me}><AiScopeProvider><AssistantPanelProvider><VoiceControlProvider>
      {usePhone() ? <PhoneShell>{p.children}</PhoneShell> : <DesktopShell {...p} />}
      <ActivityOverlay me={p.me} />
    </VoiceControlProvider></AssistantPanelProvider></AiScopeProvider></DockProvider>
  );
}

function DesktopShell({ me, theme, onTheme, children }: { me: SessionUser; theme: 'light' | 'dark'; onTheme: () => void; children: ReactNode }) {
  const { data: spaces } = useSpaces();
  const { data: sum } = useSummary();
  const [creating, setCreating] = useState(false);
  // What the assistant found that nobody has kept or dismissed yet (partners: always 0, they see kept only).
  const fresh = useQuery({ queryKey: ['research-new'], queryFn: api.research.newCount, refetchInterval: 60_000 }).data?.n ?? 0;
  // The blue "new" dots start here (6.3): a space with something someone else added that you have not seen, and the
  // Knowledge base and the Research Center. They lead down to the thing itself (the space's track and row).
  const dots = useNewSince();
  const nav = useNavigate();
  const vc = useVoiceControl();
  const cls = ({ isActive }: { isActive: boolean }) => `cnav${isActive ? ' on' : ''}`;
  // Search opens over the page (the sidebar's row, "/" or Cmd/Ctrl+K; the keys live with the modal).
  // Ctrl/Cmd+Shift+V starts or stops voice control (not in a text field, where it pastes plain text).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey || e.key.toLowerCase() !== 'v') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      if (vc.on) vc.stop(); else vc.start();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [vc]);
  const signOut = async () => { await api.auth.logout().catch(() => undefined); nav('/login'); location.reload(); };

  // The sidebar folds to a rail of icons (Kareem, 4 Nov): the toggle at its top, or Ctrl/Cmd+B. Remembered per person.
  const [rail, toggleRail] = useRail(me.id);
  const L = ({ t }: { t: string }) => <span className="nl">{t}</span>;
  const tip = (t: string) => (rail ? { 'data-tip': t } : {});
  // The rail's tooltips: the name of what is under the pointer or focus, beside the rail (fixed, so the rail's scroll
  // never cuts it off).
  const [tipNow, setTipNow] = useState<{ text: string; y: number } | null>(null);
  const tipAt = (e: { target: EventTarget }) => {
    const el = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-tip]') : null;
    if (!el) { setTipNow(null); return; }
    const r = el.getBoundingClientRect();
    setTipNow({ text: el.dataset.tip ?? '', y: r.top + r.height / 2 });
  };

  return (
    <div className={`shell${rail ? ' railed' : ''}`}>
      <aside className={`cside${rail ? ' rail' : ''}`} aria-label="Main" onMouseOver={tipAt} onFocus={tipAt} onMouseLeave={() => setTipNow(null)} onBlur={() => setTipNow(null)}>
        <div className="stop">
          <NavLink className="clogo" to="/"><i /><span className="nl">Delta Partners</span></NavLink>
          {!rail && <NavLink className="ib bell" to="/notifications" aria-label={`Notifications, ${sum?.notifications ?? 0} to dismiss`}>
            <IconBell /><span className="bdg">{sum?.notifications ? sum.notifications : ''}</span>
          </NavLink>}
        </div>
        <button type="button" className="cnav sidetog" onClick={toggleRail} aria-expanded={!rail} aria-label={rail ? 'Expand the sidebar' : 'Collapse the sidebar'}
          {...tip(`Expand (${modKey()}B)`)} title={rail ? undefined : `Collapse the sidebar (${modKey()}B)`}>
          <IconSide /><L t={rail ? 'Expand' : 'Collapse'} />
        </button>
        {rail && <NavLink className="cnav" to="/notifications" aria-label={`Notifications, ${sum?.notifications ?? 0} to dismiss`} {...tip('Notifications')}><IconBell />{sum?.notifications ? <span className="rdot" aria-hidden="true" /> : null}</NavLink>}
        <button type="button" className="cnav srow" onClick={openPalette} aria-haspopup="dialog" aria-keyshortcuts="/ Control+K Meta+K" {...tip('Search (/)')}
          title={rail ? undefined : `Search everything (/ or ${modKey()}K)`}><IconSearch /><L t="Search" /><kbd aria-hidden="true">/</kbd></button>
        <NavLink className={cls} to="/" end {...tip('Today')}><IconHome /><L t="Today" /></NavLink>
        <NavLink className={cls} to="/calendar" {...tip('Calendar')}><IconCalendar /><L t="Calendar" /></NavLink>
        <NavLink className={cls} to="/meetings" end={false} {...tip('Meetings')}><IconChat /><L t="Meetings" /></NavLink>
        <NavLink className={cls} to="/reminders" {...tip('Reminders')}><IconClock /><L t="Reminders" />{sum?.overdueReminders ? <span className="ct n">{sum.overdueReminders}</span> : null}</NavLink>
        <NavLink className={cls} to="/assistant" {...tip('Assistant')}><span className="orbi"><Orb size={12} /></span><L t="Assistant" /></NavLink>
        <NavLink className={cls} to="/requests" {...tip('Requests')}><IconInbox /><L t="Requests" />{sum?.waitingActions ? <span className="ct"><Count value={sum.waitingActions} /></span> : null}</NavLink>
        <div className="nsec">Spaces</div>
        {(spaces ?? []).map(s => (
          <NavLink key={s.id} className={cls} to={`/spaces/${s.id}`} {...tip(s.name)} data-new={dots.space(s.id) || undefined}><span className={`dt ${shownSpaceColor(s.color)}`} /><L t={s.name} />{dots.space(s.id) && <NewDot />}</NavLink>
        ))}
        {spaces && !spaces.length && !rail && (
          <div className="snone" role="group" aria-label="No spaces yet">
            <span className="xs mut">{createsSpaces(me) ? 'No spaces yet' : 'No spaces yet. The director adds you to one.'}</span>
            {createsSpaces(me) && <AddButton className="sm" onClick={() => setCreating(true)}>New space</AddButton>}
          </div>
        )}
        <div className="nsec">Knowledge</div>
        <NavLink className={cls} to="/folders" {...tip('Knowledge base')} data-new={dots.kb || undefined}><IconFolder /><L t="Knowledge base" />{dots.kb && <NewDot />}</NavLink>
        <NavLink className={cls} to="/knowledge" {...tip('Research Center')} data-new={dots.research || undefined}><IconBook /><L t="Research Center" />{dots.research && <NewDot />}{fresh ? <span className="ct ai">{fresh} new</span> : null}</NavLink>
        <div className="sbot">
          {/* Voice control (Kareem, 4 Nov): not a nav row; a pill of its own above Record a meeting. */}
          <button type="button" className={`vcpill${vc.on ? ' on' : ''}`} aria-pressed={vc.on} aria-label={vc.on ? 'Stop voice control' : 'Voice control'} onClick={vc.on ? vc.stop : vc.start}
            {...tip(`Voice control (${vcKey()})`)} title={rail ? undefined : 'Run the app by voice: go to pages, find things, read out the screen, approve cards'}>
            {vc.on ? <><span className="vco"><Orb size={12} state="listening" /></span><span className="nl vcl">Listening</span><span className="vstop nl">Stop</span></>
              : <><IconMicNav /><span className="nl vcl">Voice control</span><kbd className="nl">{vcKey()}</kbd></>}
          </button>
          <NavLink className={cls} to="/record" {...tip('Record a meeting')}><IconRec /><L t="Record a meeting" /></NavLink>
          {/* Admins: what the app is used for and what it costs (6.3: a row of its own, near Settings). */}
          {me.isAdmin && <NavLink className={cls} to="/settings/usage" {...tip('Usage')}><IconUsage /><L t="Usage" /></NavLink>}
          <NavLink className={cls} to="/settings" end {...tip('Settings')}><IconGear /><L t="Settings" /></NavLink>
        </div>
        <div className="sfoot">
          <span className="who2 lg" aria-hidden="true" title={rail ? me.name : undefined}>{me.initials}</span>
          <div className="nl" style={{ flex: 1, minWidth: 0 }}>
            <div className="sm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.name}</div>
            <div className="xs mut">{ROLE[me.role]}{me.isAdmin ? ', admin' : ''}</div>
            <button type="button" className="signout xs" onClick={() => void signOut()} aria-label={`Sign out ${me.name}`}>Sign out</button>
          </div>
          {!rail && <button className="ib" onClick={onTheme} aria-label="Switch light or dark" style={{ width: 34, height: 34 }}>{theme === 'dark' ? <IconSun /> : <IconMoon />}</button>}
        </div>
      </aside>
      {rail && tipNow && <div className="railtip" role="tooltip" style={{ top: tipNow.y }}>{tipNow.text}</div>}
      <Glide as="main" className="cmain"><AiButton phone={false} />{children}</Glide>
      <DockArea me={me} />
      <CommandPalette />
      <AnimatePresence>{creating && <SpaceSheet key="new-space" me={me} onClose={() => setCreating(false)} />}</AnimatePresence>
    </div>
  );
}

/** The sidebar folded to a rail, remembered for this person on this browser; Ctrl/Cmd+B toggles it anywhere. */
function useRail(userId: string): [boolean, () => void] {
  const key = `dpw.sidebar.${userId}`;
  const [rail, setRail] = useState(() => { try { return localStorage.getItem(key) === 'rail'; } catch { return false; } });
  const toggle = useCallback(() => setRail(r => {
    const next = !r;
    try { localStorage.setItem(key, next ? 'rail' : 'full'); } catch { /* storage blocked: it still folds for now */ }
    return next;
  }), [key]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') { e.preventDefault(); toggle(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);
  return [rail, toggle];
}

/** Voice control's shortcut, as this computer writes it. */
const vcKey = () => (modKey() === '⌘' ? '⌘⇧V' : 'Ctrl⇧V');

const IconSide = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M9 4v16" /></svg>;
const IconUsage = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>;
const IconMicNav = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3h6v11H9zM5 11a7 7 0 0 0 14 0M12 18v3" /></svg>;

export { Avatar };
